import { useState, useMemo } from 'react';
import {
  ChevronRight,
  ChevronUp,
  ChevronDown,
  MoreHorizontal,
  ArrowUpDown,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { Task, TaskStatus } from '@/types/index';

// ─── Props ───────────────────────────────────────────────────────────────────

interface FlatTableProps {
  tasks: Task[];
  allTasks: Task[];
  onSelectTask: (id: string) => void;
  selectedTaskId?: string | null;
  onEditTask: (id: string) => void;
  onAddSubtask: (id: string) => void;
  onDeleteTask: (id: string) => void;
}

// ─── Sort types ──────────────────────────────────────────────────────────────

type SortColumn = 'path' | 'title' | 'status' | 'owner' | 'depth' | 'createdAt';
type SortDirection = 'asc' | 'desc';

// ─── Status badge config ─────────────────────────────────────────────────────

const statusConfig: Record<
  TaskStatus,
  { label: string; className: string }
> = {
  pending: {
    label: 'Pending',
    className: 'bg-gray-500/10 text-gray-500 dark:text-gray-400',
  },
  in_progress: {
    label: 'In Progress',
    className: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  },
  blocked: {
    label: 'Blocked',
    className: 'bg-red-500/10 text-red-600 dark:text-red-400',
  },
  completed: {
    label: 'Completed',
    className: 'bg-green-500/10 text-green-600 dark:text-green-400',
  },
  cancelled: {
    label: 'Cancelled',
    className:
      'bg-gray-500/10 text-gray-500 line-through dark:text-gray-400',
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function truncate(text: string, maxLen: number): string {
  if (text.length <= maxLen) return text;
  return text.slice(0, maxLen - 1) + '\u2026';
}

/**
 * Build a breadcrumb path for a task by resolving IDs to titles.
 * Uses the materialized `path` field when available, otherwise walks
 * up the `parentId` chain via the `allTasks` lookup map.
 */
function buildBreadcrumb(
  task: Task,
  taskMap: Map<string, Task>,
): Task[] {
  // Strategy 1 – materialized path (e.g. "rootId/childId/currentId")
  if (task.path) {
    const ids = task.path.split('/').filter(Boolean);
    const chain: Task[] = [];
    for (const id of ids) {
      const found = taskMap.get(id);
      if (found) chain.push(found);
    }
    if (chain.length > 0) return chain;
  }

  // Strategy 2 – walk up parentId chain
  const chain: Task[] = [];
  let current: Task | undefined = task;
  const visited = new Set<string>();

  while (current) {
    if (visited.has(current._id)) break; // guard against cycles
    visited.add(current._id);
    chain.unshift(current);
    current = current.parentId ? taskMap.get(current.parentId) : undefined;
  }

  return chain;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: TaskStatus }) {
  const config = statusConfig[status];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        config.className,
      )}
    >
      {config.label}
    </span>
  );
}

function SortIndicator({
  column,
  activeColumn,
  direction,
}: {
  column: SortColumn;
  activeColumn: SortColumn;
  direction: SortDirection;
}) {
  if (column !== activeColumn) {
    return <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/50" />;
  }
  return direction === 'asc' ? (
    <ChevronUp className="ml-1 h-3.5 w-3.5 text-primary" />
  ) : (
    <ChevronDown className="ml-1 h-3.5 w-3.5 text-primary" />
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function FlatTable({
  tasks,
  allTasks,
  onSelectTask,
  selectedTaskId,
  onEditTask,
  onAddSubtask,
  onDeleteTask,
}: FlatTableProps) {
  const [sortColumn, setSortColumn] = useState<SortColumn>('path');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Build a lookup map for path resolution
  const taskMap = useMemo(() => {
    const map = new Map<string, Task>();
    for (const t of allTasks) {
      map.set(t._id, t);
    }
    return map;
  }, [allTasks]);

  // Pre-compute breadcrumbs for every visible task
  const tasksWithBreadcrumbs = useMemo(() => {
    return tasks.map((task) => ({
      task,
      breadcrumb: buildBreadcrumb(task, taskMap),
    }));
  }, [tasks, taskMap]);

  // Sorted rows
  const sortedRows = useMemo(() => {
    const rows = [...tasksWithBreadcrumbs];

    rows.sort((a, b) => {
      let cmp = 0;

      switch (sortColumn) {
        case 'path': {
          const pathA = a.breadcrumb.map((t) => t.title).join('/');
          const pathB = b.breadcrumb.map((t) => t.title).join('/');
          cmp = pathA.localeCompare(pathB);
          break;
        }
        case 'title':
          cmp = a.task.title.localeCompare(b.task.title);
          break;
        case 'status':
          cmp = a.task.status.localeCompare(b.task.status);
          break;
        case 'owner':
          cmp = a.task.ownerId.localeCompare(b.task.ownerId);
          break;
        case 'depth':
          cmp = a.task.depth - b.task.depth;
          break;
        case 'createdAt':
          cmp =
            new Date(a.task.createdAt).getTime() -
            new Date(b.task.createdAt).getTime();
          break;
      }

      return sortDirection === 'asc' ? cmp : -cmp;
    });

    return rows;
  }, [tasksWithBreadcrumbs, sortColumn, sortDirection]);

  // Toggle sort on a column
  function handleSort(column: SortColumn) {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }

  // ─── Empty state ─────────────────────────────────────────────────────────

  if (tasks.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
        No tasks found
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="overflow-auto rounded-md border">
      <table className="w-full text-sm">
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <thead className="sticky top-0 z-10 bg-background">
          <tr className="border-b">
            <th
              className="cursor-pointer px-3 py-2 text-left font-medium text-muted-foreground hover:text-foreground"
              onClick={() => handleSort('path')}
            >
              <span className="flex items-center">
                Path
                <SortIndicator
                  column="path"
                  activeColumn={sortColumn}
                  direction={sortDirection}
                />
              </span>
            </th>
            <th
              className="w-[200px] cursor-pointer px-3 py-2 text-left font-medium text-muted-foreground hover:text-foreground"
              onClick={() => handleSort('title')}
            >
              <span className="flex items-center">
                Title
                <SortIndicator
                  column="title"
                  activeColumn={sortColumn}
                  direction={sortDirection}
                />
              </span>
            </th>
            <th
              className="w-[120px] cursor-pointer px-3 py-2 text-left font-medium text-muted-foreground hover:text-foreground"
              onClick={() => handleSort('status')}
            >
              <span className="flex items-center">
                Status
                <SortIndicator
                  column="status"
                  activeColumn={sortColumn}
                  direction={sortDirection}
                />
              </span>
            </th>
            <th
              className="w-[100px] cursor-pointer px-3 py-2 text-left font-medium text-muted-foreground hover:text-foreground"
              onClick={() => handleSort('owner')}
            >
              <span className="flex items-center">
                Owner
                <SortIndicator
                  column="owner"
                  activeColumn={sortColumn}
                  direction={sortDirection}
                />
              </span>
            </th>
            <th
              className="w-[60px] cursor-pointer px-3 py-2 text-left font-medium text-muted-foreground hover:text-foreground"
              onClick={() => handleSort('depth')}
            >
              <span className="flex items-center">
                Depth
                <SortIndicator
                  column="depth"
                  activeColumn={sortColumn}
                  direction={sortDirection}
                />
              </span>
            </th>
            <th
              className="w-[120px] cursor-pointer px-3 py-2 text-left font-medium text-muted-foreground hover:text-foreground"
              onClick={() => handleSort('createdAt')}
            >
              <span className="flex items-center">
                Created
                <SortIndicator
                  column="createdAt"
                  activeColumn={sortColumn}
                  direction={sortDirection}
                />
              </span>
            </th>
            <th className="w-[60px] px-3 py-2 text-left font-medium text-muted-foreground">
              Actions
            </th>
          </tr>
        </thead>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <tbody>
          {sortedRows.map(({ task, breadcrumb }) => {
            const isSelected = task._id === selectedTaskId;

            return (
              <tr
                key={task._id}
                className={cn(
                  'border-b transition-colors even:bg-muted/50',
                  isSelected && 'bg-primary/10',
                )}
              >
                {/* Path / breadcrumb */}
                <td className="px-3 py-2">
                  <div className="flex flex-wrap items-center gap-1">
                    {breadcrumb.map((ancestor, idx) => (
                      <span key={ancestor._id} className="flex items-center gap-1">
                        {idx > 0 && (
                          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground/60" />
                        )}
                        <span
                          className={cn(
                            'text-xs',
                            idx === breadcrumb.length - 1
                              ? 'font-medium text-foreground'
                              : 'text-muted-foreground',
                          )}
                        >
                          {truncate(ancestor.title, 20)}
                        </span>
                      </span>
                    ))}
                  </div>
                </td>

                {/* Title (clickable) — with depth indentation */}
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5" style={{ paddingLeft: `${task.depth * 16}px` }}>
                    {task.depth > 0 && (
                      <span className={cn(
                        'inline-block h-3 w-0.5 shrink-0 rounded-full',
                        task.depth === 1 && 'bg-blue-300 dark:bg-blue-700',
                        task.depth === 2 && 'bg-purple-300 dark:bg-purple-700',
                        task.depth >= 3 && 'bg-green-300 dark:bg-green-700',
                      )} />
                    )}
                    <button
                      type="button"
                      className="max-w-[200px] truncate text-left text-sm font-medium text-foreground hover:underline"
                      onClick={() => onSelectTask(task._id)}
                      title={task.title}
                    >
                      {task.title}
                    </button>
                  </div>
                </td>

                {/* Status */}
                <td className="px-3 py-2">
                  <StatusBadge status={task.status} />
                </td>

                {/* Owner */}
                <td className="px-3 py-2">
                  <span className="inline-flex items-center rounded-md bg-secondary px-1.5 py-0.5 text-xs font-medium text-secondary-foreground">
                    {task.ownerId.slice(0, 6)}
                  </span>
                </td>

                {/* Depth */}
                <td className="px-3 py-2 text-center tabular-nums text-muted-foreground">
                  {task.depth}
                </td>

                {/* Created */}
                <td className="px-3 py-2 text-xs text-muted-foreground" title={task.createdAt}>
                  {formatDistanceToNow(new Date(task.createdAt), { addSuffix: true })}
                </td>

                {/* Actions */}
                <td className="px-3 py-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => onEditTask(task._id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditTask(task._id);
                        }}
                      >
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => onAddSubtask(task._id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddSubtask(task._id);
                        }}
                      >
                        Add subtask
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onSelect={() => onDeleteTask(task._id)}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteTask(task._id);
                        }}
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
